import { type DictionarySource, tierFiles } from "@kensio/pinyinjs";

import type { CachedFiles, DictionaryCache } from "./dictionary-cache.js";
import type { ServedTier } from "./dictionary.js";

/**
 * Where this site's dictionary files are, and how they are read.
 *
 * Apart from `dictionary.ts` because it answers a different question. That one
 * is about tiers: which is loaded, which is in memory, which is on the device.
 * This one is about files: what they are called once published, what address
 * each sits at, and whether a given read has to touch the network.
 *
 * Nothing above the `DictionarySource` interface knows any of it, which is what
 * the interface is for: the package's decoder asks for `standard.keys` and does
 * not care that this site serves it as `standard.keys.txt`, brotli-compressed,
 * possibly out of a store on the visitor's disk.
 */

/**
 * How the artifacts are named on this site.
 *
 * The package's browser loader fetches `standard.entries`, `standard.keys` and
 * `script.map` as they are named on disk. This site publishes them with a
 * `.txt` suffix so that S3 reports them as text and CloudFront compresses them,
 * see `src/assets/dictionary-data.ts` where the renaming happens, so the source
 * has to know about the suffix. `.freq` is binary and is published unchanged.
 */
function publishedName(name: string): string {
  return name.endsWith(".freq") ? name : `${name}.txt`;
}

/** Where an artifact of a given name is published. */
export function publishedUrl(baseUrl: string, name: string): string {
  return `${baseUrl.replace(/\/$/u, "")}/${publishedName(name)}`;
}

/**
 * What every published dictionary starts with, whichever version it is.
 *
 * Kept in step by hand with `dictionaryUrl` in `src/assets/dictionary-data.ts`,
 * which writes the path this is the fixed half of. What it is for is the
 * pruning in `dictionary-cache.ts`, which needs to know a superseded
 * dictionary when it sees one without being told the versions there have been.
 */
export const publishedDictionaries = "/data/pinyinjs-";

/** The dictionary files this page serves, and the ones it supersedes. */
export function cachedDictionaries(baseUrl: string): CachedFiles {
  return {
    family: publishedDictionaries,
    current: `${baseUrl.replace(/\/$/u, "")}/`,
  };
}

/**
 * The three files a tier is made of, at the addresses this site serves them.
 *
 * From the package's own `tierFiles` rather than a list written out here, so
 * that "is this tier on the device" is a question about exactly the files the
 * loader would fetch, by construction rather than by agreement.
 */
export function tierUrls(baseUrl: string, tier: ServedTier): readonly string[] {
  const files = tierFiles(tier);

  return [files.keys, files.entries, files.frequencies].map((name) =>
    publishedUrl(baseUrl, name),
  );
}

/**
 * A `DictionarySource` over this site's published artifacts.
 *
 * The package defines the source as two functions for exactly this reason: the
 * shape of the URLs is the site's business, and nothing above this interface
 * needs to know that two of the three files were renamed on the way out, or
 * that a file may not be coming over the network at all.
 */
export function siteSource(
  baseUrl: string,
  cache?: DictionaryCache,
): DictionarySource {
  const get = async (name: string): Promise<Response> => {
    const url = publishedUrl(baseUrl, name);

    const stored = await cache?.match(url);
    if (stored !== undefined) {
      return stored;
    }

    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(
        `fetching ${url} failed: ${String(response.status)} ${response.statusText}`,
      );
    }

    // Not awaited. The copy going to disk is not something the conversion is
    // waiting on, and `keep` swallows its own failures, so there is nothing
    // here to wait for and nothing to handle.
    void cache?.keep(url, response.clone());

    return response;
  };

  return {
    text: async (name: string): Promise<string> => {
      const response = await get(name);

      return response.text();
    },
    bytes: async (name: string): Promise<Uint8Array> => {
      const response = await get(name);

      return new Uint8Array(await response.arrayBuffer());
    },
  };
}
